import { Component, computed, input, OnChanges, OnInit, output, signal, SimpleChanges } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import moment from 'moment';

@Component({
  selector: 'app-month-pager',
  imports: [MatIcon],
  templateUrl: './month-pager.component.html',
  styleUrl: './month-pager.component.scss',
})
export class MonthPagerComponent implements OnInit, OnChanges {
  monthChange = output<moment.Moment>();
  availableMonths = input<string[] | null>(null);

  private selectedMonth = signal<moment.Moment>(moment());

  private sortedAvailableMonths = computed(() => {
    const months = this.availableMonths();
    if (!months) return null;
    return [...months].sort();
  });

  monthTitle = computed(() => this.selectedMonth().format('MMMM YYYY'));

  isPrevDisabled = computed(() => {
    const sorted = this.sortedAvailableMonths();
    if (!sorted) return false;
    const current = this.selectedMonth().format('YYYY-MM');
    return sorted.indexOf(current) <= 0;
  });

  isNextDisabled = computed(() => {
    const sorted = this.sortedAvailableMonths();
    if (!sorted) return this.selectedMonth().isSame(moment(), 'month');
    const current = this.selectedMonth().format('YYYY-MM');
    const idx = sorted.indexOf(current);
    return idx === -1 || idx >= sorted.length - 1;
  });

  ngOnInit(): void {
    this.monthChange.emit(this.selectedMonth());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['availableMonths']) return;
    const sorted = this.sortedAvailableMonths();
    if (!sorted || sorted.length === 0) return;
    const current = this.selectedMonth().format('YYYY-MM');
    if (!sorted.includes(current)) {
      this.selectedMonth.set(moment(sorted[sorted.length - 1], 'YYYY-MM'));
      this.monthChange.emit(this.selectedMonth());
    }
  }

  prevMonth(): void {
    const sorted = this.sortedAvailableMonths();
    if (sorted) {
      const current = this.selectedMonth().format('YYYY-MM');
      const idx = sorted.indexOf(current);
      if (idx > 0) {
        this.selectedMonth.set(moment(sorted[idx - 1], 'YYYY-MM'));
        this.monthChange.emit(this.selectedMonth());
      }
    } else {
      this.selectedMonth.update((month) => month.clone().subtract(1, 'month'));
      this.monthChange.emit(this.selectedMonth());
    }
  }

  nextMonth(): void {
    const sorted = this.sortedAvailableMonths();
    if (sorted) {
      const current = this.selectedMonth().format('YYYY-MM');
      const idx = sorted.indexOf(current);
      if (idx < sorted.length - 1) {
        this.selectedMonth.set(moment(sorted[idx + 1], 'YYYY-MM'));
        this.monthChange.emit(this.selectedMonth());
      }
    } else {
      if (this.isNextDisabled()) return;
      this.selectedMonth.update((month) => month.clone().add(1, 'month'));
      this.monthChange.emit(this.selectedMonth());
    }
  }

  goToCurrentMonth(): void {
    const sorted = this.sortedAvailableMonths();
    if (sorted && sorted.length > 0) {
      const currentMonthStr = moment().format('YYYY-MM');
      const target = sorted.includes(currentMonthStr) ? currentMonthStr : sorted[sorted.length - 1];
      this.selectedMonth.set(moment(target, 'YYYY-MM'));
    } else {
      this.selectedMonth.set(moment());
    }
    this.monthChange.emit(this.selectedMonth());
  }
}
